import { useSnackbarStore } from '@/modules/global/stores/SnackbarStore'
import { useWebSocketStore } from '@/modules/global/stores/WebSocketStore'
import i18n from '@/util/config/i18n'
import { defineStore } from 'pinia'

export interface PdfExportEntry {
  spaceId: string
  pageId?: string
  /** `lost`: the socket dropped while the user was on another page; it restarts when they return to the space */
  socket: 'live' | 'lost'
}

// Not reactive state: a running export owns a controller (stopped on logout) and a "check the job now" callback
const controllers = new Map<string, AbortController>()
const nudges = new Map<string, () => void>()
let listeningForSocketLoss = false

/** The id of the space the user is looking at, or null on any other page */
async function resolveRouteSpaceId(): Promise<string | null> {
  try {
    const router = (await import('@/router')).default
    const { getSpaceIdFromRoute } = await import('@/router/helpers')
    return getSpaceIdFromRoute(router.currentRoute) || null
  } catch {
    return null
  }
}

/**
 * Background PDF exports that are in flight, one entry per target (`spaceId`, or `spaceId:pageId` for a page).
 * Lives outside any component so an export keeps running, and keeps being shown, while the user navigates.
 */
export const usePdfExportStore = defineStore('pdfExport', {
  state: (): { exports: Record<string, PdfExportEntry> } => ({ exports: {} }),
  getters: {
    isSpaceExporting:
      state =>
      (spaceId: string): boolean =>
        Object.values(state.exports).some(entry => entry.spaceId === spaceId),
    /** Exact target key: `spaceId` for a whole space, `spaceId:pageId` for one page */
    isTargetExporting:
      state =>
      (key: string): boolean =>
        !!state.exports[key],
    exportingSpaceIds: state => Array.from(new Set(Object.values(state.exports).map(entry => entry.spaceId)))
  },
  actions: {
    /**
     * Registers an export. Returns its abort signal, or null when that same target is already exporting.
     * Other targets are never affected: exports run in parallel.
     */
    begin(key: string, meta: { spaceId: string; pageId?: string }): AbortSignal | null {
      if (this.exports[key]) return null
      const controller = new AbortController()
      controllers.set(key, controller)
      this.exports = { ...this.exports, [key]: { ...meta, socket: 'live' } }
      this.listenForSocketLoss()
      return controller.signal
    },

    end(key: string) {
      controllers.delete(key)
      nudges.delete(key)
      if (!this.exports[key]) return
      const rest = { ...this.exports }
      delete rest[key]
      this.exports = rest
    },

    /** Stops every export silently (logout): nothing may navigate or notify in the next session */
    abortAll() {
      controllers.forEach(controller => controller.abort())
      controllers.clear()
      nudges.clear()
      this.exports = {}
    },

    /** `check` re-reads the job now; called after the socket restarted so a push missed meanwhile is not lost */
    registerNudge(key: string, check: () => void) {
      nudges.set(key, check)
    },

    listenForSocketLoss() {
      // Once for the app's lifetime: the listener is not tied to any component
      if (listeningForSocketLoss) return
      listeningForSocketLoss = true
      useWebSocketStore().onUnexpectedClose(async () => {
        const spaceId = await resolveRouteSpaceId()
        usePdfExportStore().onSocketLost(spaceId)
      })
    },

    /**
     * The socket dropped. On the exporting space: restart it at once. Elsewhere: tell the user once and leave it
     * until they open the space (see resync).
     */
    onSocketLost(currentSpaceId: string | null) {
      const keys = Object.keys(this.exports)
      const onSpace = keys.filter(key => this.exports[key].spaceId === currentSpaceId)
      const away = keys.filter(
        key => this.exports[key].spaceId !== currentSpaceId && this.exports[key].socket === 'live'
      )

      if (onSpace.length) {
        this.restartSocketAndCheck(onSpace)
      }
      if (away.length) {
        const patch = { ...this.exports }
        away.forEach(key => (patch[key] = { ...patch[key], socket: 'lost' }))
        this.exports = patch
        new Set(away.map(key => this.exports[key].spaceId)).forEach(spaceId =>
          useSnackbarStore().enqueue({
            label: i18n.t('signatureList.pdfExportConnectionLost') as string,
            leadingIcon: true,
            variant: 'warning',
            dedupeKey: `pdf-export-socket-lost-${spaceId}`
          })
        )
      }
    },

    /** The user opened a space: exports of it that lost their socket restart it and fetch the job's update */
    resync(spaceId: string) {
      const lost = Object.keys(this.exports).filter(
        key => this.exports[key].spaceId === spaceId && this.exports[key].socket === 'lost'
      )
      if (!lost.length) return
      const patch = { ...this.exports }
      lost.forEach(key => (patch[key] = { ...patch[key], socket: 'live' }))
      this.exports = patch
      this.restartSocketAndCheck(lost)
    },

    restartSocketAndCheck(keys: string[]) {
      // Subscriptions replay on connect (WebSocketStore.initializeSubscriptions)
      useWebSocketStore().connectWebSocket()
      keys.forEach(key => nudges.get(key)?.())
    }
  }
})

export type PdfExportStore = ReturnType<typeof usePdfExportStore>
